import { parseNumber } from './format.js';
import { Text } from '../../../typography/text/Text.js';
import { getI18nErrorMessage } from '../../forms-core/hooks/useFormFieldValidation.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../../forms-core/state/formfield-messages-state-reducer.js';
import type { NativeValidityStateKeys } from '../../validation/types.js';

/**
 * Sanitizes a numeric step value, returning 1 if the value is undefined, NaN, zero, or negative.
 * @internal
 */
export function sanitizePositiveNumber(value: number | undefined): number {
  return value !== undefined && !Number.isNaN(value) && value > 0 ? value : 1;
}

/**
 * Checks if the given value is aligned with the specified step and minimum value.
 * @internal
 */
export function isStepAligned(
  value: number,
  step: number,
  min: number = 0,
): boolean {
  step = sanitizePositiveNumber(step);
  const roundedValue = Math.round((value - min) / step) * step + min;
  const tolerance = step * 1e-9;
  return Math.abs(value - roundedValue) < tolerance;
}

/**
 * Determines the appropriate error key based on the provided value and validation constraints (min, max, step).
 * @internal
 */
export function getErrorKey(
  value: string | number,
  min?: number,
  max?: number,
  step?: number,
): keyof typeof NativeValidityStateKeys | null {
  const parsed = parseNumber(value);

  if (min !== undefined && parsed < min) {
    return 'rangeUnderflow';
  }

  if (max !== undefined && parsed > max) {
    return 'rangeOverflow';
  }

  if (step && step > 0 && !isStepAligned(parsed, step, min ?? 0)) {
    return 'stepMismatch';
  }
  return null;
}

/** @internal */
export function addError(
  errorKey: keyof typeof NativeValidityStateKeys,
  id: string,
  input: HTMLInputElement | HTMLSelectElement | null | undefined,
  dispatch: ((action: FormFieldMessagesReducerAction) => void) | undefined,
  forceCustomValidity: boolean = false,
): void {
  if (forceCustomValidity) {
    dispatch?.({
      type: FormFieldMessagesAction.REMOVEMESSAGE,
      payload: { id, messageType: 'formcontrol', controlId: input?.id },
    });
  }

  dispatch?.({
    type: FormFieldMessagesAction.ADDMESSAGE,
    payload: {
      id,
      message: (
        <Text textStyle="small" as="p">
          {getI18nErrorMessage(errorKey, input)}
        </Text>
      ),
      validityStateKey: forceCustomValidity ? 'customError' : errorKey,
      messageType: 'formcontrol',
      variant: 'error',
      controlId: input?.id,
    },
  });
}
